import { logger } from 'src/logger';
import { LabCentralContext } from '../../../auth/AppContextImpl/LabCentralContext';
import { ViciContext } from '../../../auth/AppContextImpl/ViciContext';
import {
  IGetUserAuthorizedForResourcesResponse,
  ResourceAttributes,
} from 'src/state/slice/models/CaseLifecycleTypes';
import { IAuthContext } from 'src/state/providers/AuthProvider';
import { isAnonymousUser, isMonsContext } from './AuthAttributeHelper';
import { UserModal } from '../../../auth/types/UserContextTypes';
import { AnonymousUserDetails } from '../../../auth/UserDetailsImpl/AnonymousUserDetails';
import { LoggedUserDetails } from '../../../auth/UserDetailsImpl/LoggedInUserDetails';

export interface UserDetails {
  getUserModal: () => UserModal;
}

/**
 * Fetch User Details.
 * @returns UserModal
 */
export const fetchUserDetails = (): UserModal => {
  if (isAnonymousUser) {
    return new AnonymousUserDetails().getUserModal();
  }
  return new LoggedUserDetails().getUserModal();
};

/**
 * Populates the User Context.
 * There can be 3 types of users.
 * 1. Anonymous Users
 * 2. Logged-in users with no account association
 * 3. Logged-in users with account association.
 *    3.1 Can come from VICI
 *    3.2 Can come from RegulatorCentral.
 *
 * @param umsUserAttributes user details provided to populate the context
 * @returns IAuthContext
 */
export const fetchAuthContext = function (
  userContext: IGetUserAuthorizedForResourcesResponse | undefined,
  existingContext: IAuthContext,
): IAuthContext {
  //Generate the user context
  const userDetails: UserModal = fetchUserDetails();

  if (isMonsContext) {
    let data = new ViciContext(
      {
        userId: userContext?.userId!, //Mons User Context cannot be null.
        accountAttributes: userContext?.resourceList!,
      },
      existingContext,
    ).getUserContext();
    logger.info(
      'Loading VICI user context. Check log context for additional info:',
      data,
    );
    return data;
  } else {
    let data = new LabCentralContext(
      {
        userId: userContext?.userId || userDetails.customerId || '',
        accountAttributes: userContext?.resourceList!,
      },
      userDetails,
      existingContext,
    ).getUserContext();
    logger.info(
      'Loading LabCentral user context. Check log context for additional info:',
      data,
    );
    return data;
  }
};

/**
 * This method is responsible to merge the pre-populated auth context with the user authorized resources API.
 * @param userContext Response for GetUserAuthorizedResource. IGetUserAuthorizedForResourcesResponse.
 * @param existingContext existing authContext
 */
export const mergeAuthContextForPublicFacingPortals = function (
  userContext: IGetUserAuthorizedForResourcesResponse | undefined,
  existingContext: IAuthContext,
): IAuthContext {
  if (!existingContext.preferredAccountId || !userContext) {
    return existingContext;
  }
  return {
    ...existingContext,
    accountAttributes: userContext?.resourceList.filter(
      (resourceAttribute: ResourceAttributes) => {
        if (resourceAttribute.id == existingContext.preferredAccountId) {
          return resourceAttribute.attributes;
        }
      },
    ),
  };
};

export default fetchAuthContext;
